import { appConfig } from "@/commons";
import {
  ResourceAdminType,
  ResourceCashierType,
  ResourceEntityType,
  RESOURCES_ADMIN,
  RESOURCES_CASHIER,
  RESOURCES_ENTITY,
} from "@/constants/resources";
import { AuthProvider } from "react-admin";

const BACKOFFICE_ROLES = ["admin", "entity", "cashier"];

const clearAuthStorage = () => {
  localStorage.removeItem("userRole");
  localStorage.removeItem("gameooUser");
  localStorage.removeItem("game");
};

const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    let response: Response;

    try {
      response = await fetch(`${appConfig.api.baseUrl}/auth/login`, {
        method: "POST",
        body: JSON.stringify({ pseudo: username, password }),
        headers: new Headers({ "Content-Type": "application/json" }),
        credentials: "include",
      });
    } catch (networkError) {
      console.error("Network error while logging in:", networkError);
      return Promise.reject(
        new Error("Network error: could not reach the server"),
      );
    }

    if (!response.ok) {
      try {
        const errorText = await response.text();
        console.warn("Login failed:", errorText);
      } catch (e) {
        console.warn("Login failed, and could not read error message:", e);
      }

      return Promise.reject(new Error("Invalid credentials or API error"));
    }

    try {
      const { user } = await response.json();

      if (!user?.role) {
        return Promise.reject(new Error("Missing user role"));
      }

      const role = user.role.toLowerCase();

      if (!BACKOFFICE_ROLES.includes(role)) {
        return Promise.reject(
          new Error(`Role "${role}" is not allowed in backoffice`),
        );
      }

      localStorage.setItem("userRole", role);
      localStorage.setItem("gameooUser", JSON.stringify(user));

      window.dispatchEvent(new Event("gameoo-auth-changed"));

      return Promise.resolve();
    } catch (e) {
      console.error("Failed to process login response:", e);
      return Promise.reject(new Error("Login failed: invalid response format"));
    }
  },

  logout: async () => {
    try {
      await fetch(`${appConfig.api.baseUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.warn("Logout request failed:", e);
    }

    clearAuthStorage();

    return Promise.resolve();
  },

  checkError: (error) => {
    if (error.status === 401) {
      clearAuthStorage();
      return Promise.reject();
    }

    return Promise.resolve();
  },

  checkAuth: () => {
    const role = localStorage.getItem("userRole");

    if (role && BACKOFFICE_ROLES.includes(role)) {
      return Promise.resolve();
    }

    return Promise.reject();
  },

  canAccess: async ({ resource }) => {
    const role = localStorage.getItem("userRole");

    if (!resource) {
      return true;
    }

    if (role === "admin") {
      return RESOURCES_ADMIN.includes(resource as ResourceAdminType);
    }

    if (role === "entity") {
      return RESOURCES_ENTITY.includes(resource as ResourceEntityType);
    }

    if (role === "cashier") {
      return RESOURCES_CASHIER.includes(resource as ResourceCashierType);
    }

    return false;
  },

  getPermissions: async () => {
    const role = localStorage.getItem("userRole");

    if (!role) {
      return Promise.reject(new Error("No role"));
    }

    return role;
  },
};

export { authProvider };
